// Simulator - headless runner that drives one full game session using an
// AI policy instead of human input. No rendering, no audio, no particles,
// no pauses. Pure math at maximum speed.
//
// The simulator re-uses the same entity classes the real game uses
// (Player, Guard, Caravan, Projectile, Loot, World, Shop) so gameplay
// rules stay in a single source. What's different from game.js:
//
//   - No Renderer / Camera / Input / GameAudio / ParticleSystem.
//   - Flow is linear instead of state-machine driven: tick → (optional
//     wave end → shop → next wave) → next tick.
//   - Player actions come from an AIPolicy instance.
//   - SessionLogger is used exactly the same way as in the real game,
//     so simulated sessions can be analyzed with the same tooling.
//
// One Simulator instance runs one session and returns the finished
// session object. Use runBatch() from run.js to run many in a row.
//
// Known divergence from the live game:
// - The sim does drive enterShop into beginShopDraft (see below), but only
//   when the policy explicitly requests it. Builds that depend on shop
//   visits may look weaker in sim runs than live play unless the policy
//   actively decides to enter the shop.

import { World } from '../world.js';
import { Player } from '../player.js';
import { Shop } from '../shop.js';
import { spawnWave, resolveGuardCollisions, planWaveGuards, apportionGuards } from '../caravan.js';
import { performAttack, performSuper, Projectile, Bomb } from '../combat.js';
import { spawnLoot } from '../loot.js';
import { rollGuardDrop, applyPickup, PickupKind, isGoldPickup } from '../pickups.js';
import { UI, CARDS, stackScale } from '../ui.js';
import { SessionLogger, playerSnapshot, countGuardsByType } from '../session-logger.js';
import { CONST } from '../utils.js';
import { makeRng } from '../rng.js';
import { SimInput } from './sim-input.js';
import { guardBrainView, runGuardBrain } from '../guard-brain.js';
import { deepClone } from './snapshot.js';
import { WAVE_COIN_SWEEP_MAX_SEC } from '../game.js';

// Median of a numeric array. Returns null for empty input so callers can
// decide how to render "no data" (the CLI prints a dash, JSON keeps null).
function median(arr) {
  if (arr.length === 0) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// Marks on `ui` (so a wave snapshot carries it) the wave on which the last
// non-rebirth card left the deck — from there on drafts offer the rebirth
// sentinel. summary.deckEmptiedWave: that wave, 0 = the deck never ran out.
function noteDeckEmptied(ui, wave) {
  if (!ui.deckEmptiedWave && ui.deck.every(id => id === 'rebirth')) ui.deckEmptiedWave = wave;
}

export class Simulator {
  constructor(policy, options = {}) {
    this.policy = policy;
    this.dt = options.dt || 1 / 60;
    // Safety ceilings so a runaway policy doesn't loop forever.
    this.maxSteps = options.maxSteps || 60 * 60 * 40; // 40 in-game minutes
    this.maxWaves = options.maxWaves || 30;
    // Metadata attached to the logged session.
    this.commit = options.commit || 'sim';
    this.runId = options.runId || null;
    // Pre-baked cards applied before the first wave. Lets us answer
    // "is build X strong if the player already has it?" independently
    // of draft luck.
    this.startCards = options.startCards || [];
    // First-session onboarding mode (Track A, v1.3.0). When true, the
    // simulator mirrors what game.js does for `isFirstSession() === true`
    // runs: pre-applies maxHp + regen starter kit, full-heals before
    // wave 4, and biases the first two interwave drafts toward sustain
    // cards (lifesteal/regen/maxHp). Set via `firstSession: true` on the
    // run.js CLI or directly when constructing the Simulator. Defaults
    // off so combo scans and balance baselines stay unaffected.
    this.firstSession = !!options.firstSession;
    // Optional seed for reproducible runs. When set, every source of
    // randomness the sim touches (wave spawn, guard types, draft rolls,
    // loot scatter, random card picks) flows through this RNG instead of
    // Math.random(). Same seed ⇒ same summary.
    this.seed = options.seed != null ? options.seed : null;
    this.rng = this.seed != null ? makeRng(this.seed) : null;
    // Optional guard brain factory (see guard-brain.js) — replaces the
    // built-in guard tactics. Called once per run so stateful brains start
    // fresh. Not logged into the session: the caller knows what it ran.
    this.guardBrainFactory = options.guardBrain || null;
    // Wave-boundary snapshots (evolution experiments: skip the waves every
    // candidate plays identically). snapshotAtWave: N — play up to the start
    // of wave N, then return { snapshot } instead of a session. resumeFrom:
    // a snapshot — continue from there (the snapshot itself is not mutated,
    // so one snapshot serves many resumes). Same seed + same options ⇒ the
    // resumed run is byte-identical to the straight one.
    this.snapshotAtWave = options.snapshotAtWave || null;
    this.resumeFrom = options.resumeFrom || null;
    // keepPolicy: on resume, play on with the policy passed to this
    // Simulator instead of the snapshot's — lets an evolved player brain
    // take over from a prefix played by another policy.
    this.keepPolicy = !!options.keepPolicy;
    // maxWaveSec: a wave not cleared within this many seconds ends the run
    // as stalled (summary.timedOut) — stops a bot that kites forever from
    // passing for a survivor. Off when unset.
    this.maxWaveSec = options.maxWaveSec || null;
    // maxWaveSecPerGuard: the limit grows by this many seconds per guard of
    // the wave (late waves are honestly longer). Limit = maxWaveSec + k·guards.
    this.maxWaveSecPerGuard = options.maxWaveSecPerGuard || 0;
    // Training polygon (experiments only): polygon = { type, share, fromWave }
    // — from that wave on, that share of every escort is `type`. Off when
    // unset. lancerShare / lancerShareFromWave: older spelling for lancers.
    this.polygon = options.polygon
      || (options.lancerShare ? { type: 'lancer', share: options.lancerShare, fromWave: options.lancerShareFromWave || 1 } : null);
    // Rebalance experiments (~/dev/corovans-ai evo/tune.js): guardCountMul =
    // { [wave]: m } scales the wave's head count; guardTotal = { [wave]: n }
    // sets it (round(n), ≥ 1 per escort, split by apportionGuards).
    // Waves not listed play as the game does.
    this.guardCountMul = options.guardCountMul || null;
    this.guardTotal = options.guardTotal || null;
    // Heat (rebirths already taken): guards as fast as after that many
    // rebirths, from wave 1. The sim itself never rebirths. 0 = off.
    this.heat = options.heat || 0;
  }

  run() {
    const dt = this.dt;
    const rng = this.rng;
    const input = new SimInput();
    const guardBrain = this.guardBrainFactory ? this.guardBrainFactory() : null;

    let world = new World();
    let player = new Player(world.width / 2, world.height / 2);
    let shop = new Shop(world.width / 2 + 150, world.height / 2 - 140);
    let ui = new UI();

    // Logical clock so two seeded runs produce identical event timestamps
    // and durationMs. Without this, Date.now() leaks wall-clock jitter into
    // the session JSON and breaks byte-level reproducibility.
    let stepCount = 0;
    const clock = rng ? () => stepCount * dt * 1000 : null;

    let logger = new SessionLogger({ commit: this.commit, clock });
    // When seeded, force a deterministic session id so dumps diff cleanly
    // across runs. Without a seed, genId() keeps its wall-clock flavor.
    const forcedId = rng != null
      ? `sim-${this.seed}-${this.runId != null ? this.runId : 0}`
      : undefined;
    logger.startSession({
      id: forcedId,
      sim: true,
      policy: this.policy.name,
      runId: this.runId,
      maxWaves: this.maxWaves,
      startCards: this.startCards,
      firstSession: this.firstSession,
      seed: this.seed,
    });

    // Apply pre-baked starting cards. Unknown ids are ignored with a
    // warning to the logger so analysts can see something went wrong.
    // Stack diminishing returns apply the same way as live-game picks so
    // combo scans see realistic 3+3 scaling, not raw additive stacking.
    const startStacks = {};
    for (const id of this.startCards) {
      const card = CARDS.find(c => c.id === id);
      if (!card) {
        console.warn(`[sim] unknown start card id: ${id}`);
        continue;
      }
      const owned = startStacks[id] || 0;
      card.apply(player, stackScale(owned));
      startStacks[id] = owned + 1;
      // Don't count these as cardsPicked events — they're pre-bake, not
      // shop picks. They live in session.meta.startCards instead.
    }

    // First-session onboarding: starter kit + draft bias. Mirrors game.js
    // startGame() behavior. Mercy heal at W4 fires inside the spawn loop
    // below, gated by `_mercyHealUsed`.
    let _mercyHealUsed = false;
    if (this.firstSession) {
      const maxHpCard = CARDS.find(c => c.id === 'maxHp');
      const regenCard = CARDS.find(c => c.id === 'regen');
      if (maxHpCard) maxHpCard.apply(player, 1);
      if (regenCard) regenCard.apply(player, 1);
      // wave=0 marks "pre-wave" picks (see game.js for rationale).
      logger.logCardPicked('maxHp', 'common', 'starter', 0, 0);
      logger.logCardPicked('regen', 'uncommon', 'starter', 0, 0);
      ui._noviceBiasRemaining = 2;
    }

    // Mutable run state.
    let wave = 1;
    let score = 0;
    let caravans = [];
    let guards = [];
    let projectiles = [];
    let bombs = [];
    let loots = [];
    let pickups = [];
    let waveDamageTaken = 0;
    let waveStartStep = 0;
    let waveClearedSweepSec = 0;
    let waveStalled = false;

    const spawnCurrentWave = () => {
      caravans = spawnWave(wave, world, rng);
      guards = [];
      projectiles = [];
      bombs = [];
      loots = [];
      pickups = [];
      // Head-count cap + elite pressure — mirrors Game._spawnWave.
      const plan = planWaveGuards(wave, caravans);
      const countMul = (this.guardCountMul && this.guardCountMul[wave]) || 1;
      const total = (this.guardTotal && this.guardTotal[wave]) || plan.total;
      const counts = countMul === 1 && total === plan.total
        ? plan.counts : apportionGuards(plan.counts, total * countMul);
      for (let i = 0; i < caravans.length; i++) {
        guards.push(...caravans[i].spawnGuards(wave, rng, {
          count: counts[i],
          pressure: plan.pressure,
          heat: this.heat,
          ...(this.polygon && wave >= (this.polygon.fromWave || 1) ? { polygon: this.polygon } : {}),
        }));
      }
      waveDamageTaken = 0;
      waveStartStep = stepCount;
      waveClearedSweepSec = 0;
      // First-session mercy heal before W4 (matches game.js _spawnWave).
      if (this.firstSession && !_mercyHealUsed && wave === 4 && player.alive) {
        player.heal(player.maxHp, 'mercy');
        _mercyHealUsed = true;
      }
      player.takeHealed();
      logger.logWaveStart(wave, {
        caravans: caravans.length,
        guards: guards.length,
        boss: wave % 5 === 0,
        guardsByType: countGuardsByType(guards),
        player: playerSnapshot(player),
      });
    };

    // Drop loot for a caravan if it should — mirrors Game._checkCaravanLoot.
    const checkCaravanLoot = (caravan) => {
      if (!caravan || caravan.looted) return;
      const allGuardsDead = caravan.guards.every(g => !g.alive);
      const shouldDrop = !caravan.alive || allGuardsDead;
      if (!shouldDrop) return;
      caravan.looted = true;
      if (caravan.alive) caravan.alive = false;
      const coins = spawnLoot(caravan, rng);
      loots.push(...coins);
      logger.logCaravanRobbed(caravan.type, caravan.lootValue, wave, !!caravan.isBoss);
    };

    // Mirrors Game._onGuardKilled (sans particles/audio): log the kill,
    // drop caravan loot if due, then roll a seeded guard drop ("ништяк").
    const onGuardKilled = (guard, source) => {
      logger.logGuardKilled(guard.type, wave, source, guard.hasFiredFire);
      checkCaravanLoot(guard.caravan);
      const drop = rollGuardDrop(guard, player, wave, rng);
      if (drop) pickups.push(drop);
    };

    // Mirrors Game._onHorseHit (sans FX): telemetry for what the horse
    // soaked up and for the horse running off.
    const onHorseHit = (amount, source) => {
      if (amount > 0) logger.logHorseDamaged(amount, source, wave, player.horse ? player.horse.hp : 0);
      if (player.takeHorseLost()) logger.logHorseLost(wave);
    };

    if (this.resumeFrom) {
      // Everything a wave boundary carries over; per-wave state (caravans,
      // guards, loot, timers) is rebuilt by spawnCurrentWave below. Nothing
      // outside the guards keeps a reference to the rng, so restoring its
      // state is enough.
      const snap = deepClone(this.resumeFrom);
      ({ world, player, shop, ui, logger } = snap);
      if (!this.keepPolicy) this.policy = snap.policy;
      wave = snap.wave;
      score = snap.score;
      stepCount = snap.stepCount;
      _mercyHealUsed = snap.mercyHealUsed;
      logger._clock = clock;
      if (rng) rng.setState(snap.rngState);
      spawnCurrentWave();
      // The straight run spawns inside a loop iteration that then ticks.
      stepCount++;
    } else {
      spawnCurrentWave();
    }

    while (stepCount < this.maxSteps && wave <= this.maxWaves) {
      // --- Policy decision ----------------------------------------
      const view = {
        wave,
        player: this._playerView(player),
        caravans,
        guards,
        projectiles,
        bombs,
        loots,
        pickups,
        shop,
        shopPickAvailable: !ui.shopPickLimitReached(),
        // Pass the seeded rng (null in live game) so policies that roll
        // dice each tick (NovicePolicy brain-fart) stay reproducible.
        rng,
      };
      const action = this.policy.decidePlaying(view) || {};

      // --- Optional in-world shop visit ---------------------------
      // When the policy requests `enterShop` and the player is within
      // the shop's interact range, open the shop draft and run the
      // shop decision loop inline. Mirrors Game.openShop('world').
      if (action.enterShop && shop.isPlayerNear(player.pos) && !ui.shopPickLimitReached()) {
        // Seed the world-shop draft so reruns reproduce (mirrors the
        // interwave draft below). Without rng this fell back to Math.random
        // and broke the same-seed-byte-identical invariant whenever a
        // policy entered the world shop.
        ui.beginShopDraft(rng);
        logger.logShopOpened('shop', wave);
        const MAX_SHOP_DECISIONS = 4;
        let closed = false;
        for (let step = 0; step < MAX_SHOP_DECISIONS && !closed; step++) {
          const shopOffer = ui.draftOffer.slice();
          const shopView = {
            wave,
            mode: 'shop',
            offer: shopOffer,
            costs: shopOffer.map(c => ui.getCardCost(c)),
            player: this._playerView(player),
            gold: player.gold,
            rng,
          };
          const decision = this.policy.decideShop(shopView) || { action: 'skip' };
          if (decision.action === 'pick' && typeof decision.index === 'number') {
            const card = ui.draftOffer[decision.index];
            const pickCost = card ? ui.getCardCost(card) : 0;
            if (card && ui.pickCard(decision.index, player)) {
              noteDeckEmptied(ui, wave);
              logger.logCardPicked(card.id, card.rarity, 'shop', pickCost, wave);
              logger.logGoldSpent(pickCost);
            }
            closed = true;
          } else if (decision.action === 'reroll') {
            const rrCost = ui.getRerollCost();
            if (ui.tryReroll(player, rng)) {
              logger.logReroll('shop', rrCost, wave);
              logger.logGoldSpent(rrCost);
            } else {
              closed = true;
            }
          } else {
            closed = true;
          }
        }
        logger.logShopClosed('shop', wave);
        // Skip the rest of this tick — match Game.openShop which calls
        // input.endFrame() and yields to the next frame.
        stepCount++;
        continue;
      }
      input.setMove(action.moveX || 0, action.moveY || 0);

      // Dash before player.update so the new velocity applies this tick.
      if (action.dash && player.dashCooldownTimer <= 0 && player.alive) {
        if (player.startDash(action.moveX || 0, action.moveY || 0)) {
          logger.logDash(wave);
        }
      }

      // --- Player update ------------------------------------------
      player.update(dt, input, world.width, world.height);
      if (!player.alive) break;

      // --- Super strike (mirrors Game._fireSuper) -----------------
      if (action.super && player.trySuper()) {
        const hits = performSuper(player, guards, caravans, rng);
        let kills = 0;
        for (const hit of hits) {
          logger.logDamageDealt(hit.damage);
          if (hit.type === 'guard' && !hit.target.alive) {
            kills++;
            onGuardKilled(hit.target, 'super');
          }
          if (hit.type === 'caravan' && !hit.target.alive) checkCaravanLoot(hit.target);
        }
        logger.logSuper(wave, hits.length, kills);
      }

      // --- Attack -------------------------------------------------
      if (action.attack && player.tryAttack()) {
        logger.logAttack();
        const hits = performAttack(player, guards, caravans, rng);
        if (player.lifestealPct > 0 && hits.length > 0) {
          const totalDmg = hits.reduce((s, h) => s + h.damage, 0);
          const heal = Math.floor(totalDmg * player.lifestealPct);
          if (heal > 0) player.heal(heal, 'lifesteal');
        }
        for (const hit of hits) {
          logger.logDamageDealt(hit.damage);
          player.addSuperCharge(hit.damage);
          if (hit.type === 'guard' && !hit.target.alive) {
            onGuardKilled(hit.target, 'melee');
          }
          if (hit.type === 'caravan' && !hit.target.alive) {
            checkCaravanLoot(hit.target);
          }
        }
      }

      // --- Caravans -----------------------------------------------
      for (const c of caravans) c.update(dt, player.pos);

      // --- Guards + guard attacks ---------------------------------
      if (guardBrain) {
        runGuardBrain(guardBrain, guardBrainView(player, guards, caravans, { dt, wave, rng }));
      }
      for (const guard of guards) {
        guard.update(dt, player.pos);
        if (guard.canAttack(player.pos)) {
          const result = guard.attack(player.pos, player.vel);
          if (result.bomb) {
            bombs.push(new Bomb(
              result.origin.x, result.origin.y,
              result.target.x, result.target.y,
              result.damage
            ));
          } else if (result.ranged) {
            projectiles.push(new Projectile(
              result.origin.x, result.origin.y,
              result.dir.x, result.dir.y,
              result.damage,
              { fire: !!result.fire, sticky: !!result.sticky }
            ));
          } else {
            const actual = player.takeDamage(result.damage);
            const landed = actual + player.horseAbsorbed;
            onHorseHit(player.horseAbsorbed, `guard:${guard.type}`);
            if (actual > 0) {
              waveDamageTaken += actual;
              logger.logPlayerDamaged(actual, `guard:${guard.type}`, wave, player.hp);
            }
            if (landed > 0) {
              if (result.knockback) {
                player.applyKnockback(
                  result.knockback.x, result.knockback.y, result.knockback.force
                );
              }
              if (player.thornsPct > 0 && guard.alive) {
                const reflected = Math.max(1, Math.round(landed * player.thornsPct));
                guard.takeDamage(reflected);
                logger.logDamageReflected(reflected);
                if (!guard.alive) {
                  onGuardKilled(guard, 'thorns');
                }
              }
            }
          }
        }
      }
      if (!player.alive) break;

      resolveGuardCollisions(guards, caravans);

      // --- Projectiles --------------------------------------------
      for (let i = projectiles.length - 1; i >= 0; i--) {
        const proj = projectiles[i];
        const hit = proj.update(dt, player.pos, player.radius);
        if (hit) {
          const actual = player.takeDamage(proj.damage);
          const landed = actual + player.horseAbsorbed;
          const source = proj.fire ? 'fire-arrow' : (proj.sticky ? 'sticky-arrow' : 'arrow');
          onHorseHit(player.horseAbsorbed, source);
          if (actual > 0) {
            waveDamageTaken += actual;
            logger.logPlayerDamaged(actual, source, wave, player.hp);
          }
          // Riders only land with the hit — i-frames negate the arrow whole.
          if (landed > 0 && proj.fire) {
            player.applyBurn(CONST.FIRE_ARROW_BURN_DURATION, CONST.FIRE_ARROW_BURN_DPS);
          }
          if (landed > 0 && proj.sticky) {
            player.applySticky(CONST.STICKY_DURATION);
          }
        }
        if (!proj.alive) projectiles.splice(i, 1);
      }

      // --- Bombs --------------------------------------------------
      // Mirrors Game: the blast is a ground field, so it bypasses i-frames
      // and applies a burn on top.
      for (let i = bombs.length - 1; i >= 0; i--) {
        const bomb = bombs[i];
        if (bomb.update(dt)) {
          if (player.alive && bomb.hits(player.pos, player.radius)) {
            const actual = player.takeDamage(bomb.damage, { ignoreIframes: true });
            onHorseHit(player.horseAbsorbed, 'bomb');
            if (actual > 0) {
              waveDamageTaken += actual;
              logger.logPlayerDamaged(actual, 'bomb', wave, player.hp);
            }
            if (actual + player.horseAbsorbed > 0) {
              player.applyBurn(CONST.BOMB_BURN_DURATION, CONST.BOMB_BURN_DPS);
            }
          }
        }
        if (!bomb.alive) bombs.splice(i, 1);
      }
      if (!player.alive) break;
      // Surface burn damage applied this tick (player.update already
      // ticked the DoT — we just pull it into telemetry / wave totals).
      if (player.alive && player.burnDamageThisFrame > 0) {
        waveDamageTaken += player.burnDamageThisFrame;
        logger.logPlayerDamaged(player.burnDamageThisFrame, 'fire-burn', wave, player.hp);
      }
      if (player.burnHorseThisFrame > 0) onHorseHit(player.burnHorseThisFrame, 'fire-burn');
      if (!player.alive) break;

      // --- Loot ---------------------------------------------------
      // Mirrors Game: once the wave is cleared, every coin gets an
      // infinite magnet so the sweep finishes naturally before WAVE_END.
      const allCaravansDone = caravans.length > 0 && caravans.every(c => !c.alive);
      const allGuardsDead = caravans.length > 0 && guards.every(g => !g.alive);
      const cleared = allCaravansDone && allGuardsDead;
      const lootMagnet = cleared ? Infinity : player.magnetRangeMul;
      for (let i = loots.length - 1; i >= 0; i--) {
        const loot = loots[i];
        const collected = loot.update(
          dt, player.pos, world.width, world.height, lootMagnet
        );
        if (collected > 0) {
          player.addGold(collected);
          score += collected;
          logger.logGoldEarned(collected);
        }
        if (!loot.alive) loots.splice(i, 1);
      }

      // --- Guard-drop pickups -------------------------------------
      // Mirrors Game: heal/buff mutate the player, coin/jackpot award gold
      // through the same plumbing as coins. Gold pickups join the post-clear
      // sweep like coins; buffs/heals keep the normal magnet.
      for (let i = pickups.length - 1; i >= 0; i--) {
        const pickup = pickups[i];
        const collected = pickup.update(
          dt, player.pos, world.width, world.height,
          cleared && isGoldPickup(pickup) ? Infinity : player.magnetRangeMul
        );
        if (collected) {
          const res = applyPickup(pickup.type, player, pickup.value);
          if (res.kind === PickupKind.COIN || res.kind === PickupKind.JACKPOT) {
            player.addGold(res.gold);
            score += res.gold;
            logger.logGoldEarned(res.gold);
          }
          logger.logPickup(pickup.type.id, pickup.kind, wave);
        }
        if (!pickup.alive) pickups.splice(i, 1);
      }

      // --- Wave completion ----------------------------------------
      // Mirrors Game._tickWaveEndGate: wave ends only once guards +
      // caravans are dead AND every coin is collected. Sweep cap
      // (WAVE_COIN_SWEEP_MAX_SEC) auto-credits stragglers if a coin
      // would otherwise stall the run.
      if (caravans.length > 0) {
        if (cleared) {
          waveClearedSweepSec += dt;
        }
        const anyLootLeft = loots.some(l => l.alive) || pickups.some(isGoldPickup);
        const gateOpen = cleared && (!anyLootLeft || waveClearedSweepSec >= WAVE_COIN_SWEEP_MAX_SEC);
        if (gateOpen) {
          let pendingGold = 0;
          for (const loot of loots) {
            if (loot.alive) {
              pendingGold += loot.value;
              loot.alive = false;
            }
          }
          for (const pickup of pickups) {
            if (isGoldPickup(pickup)) pendingGold += applyPickup(pickup.type, player, pickup.value).gold;
          }
          if (pendingGold > 0) {
            player.addGold(pendingGold);
            score += pendingGold;
            logger.logGoldEarned(pendingGold);
          }
          loots = [];
          pickups = [];
          bombs = [];

          const anyRobbed = caravans.some(c => !c.escaped);
          if (waveDamageTaken === 0 && anyRobbed) {
            const bonus = CONST.FLAWLESS_WAVE_BONUS * wave;
            score += bonus;
            player.addGold(bonus);
            logger.logGoldEarned(bonus);
            logger.logFlawless(wave, bonus);
          }
          const escaped = caravans.filter(c => c.escaped).length;
          const robbed = caravans.filter(c => !c.escaped).length;
          const waveMs = Math.round((stepCount - waveStartStep) * dt * 1000);
          logger.logWaveEnd(wave, {
            durationMs: waveMs,
            activeMs: waveMs,
            damageTaken: waveDamageTaken,
            caravansRobbed: robbed,
            caravansEscaped: escaped,
            hpEnd: player.hp,
            healed: player.takeHealed(),
          });

          // End-of-wave interwave draft. Policies may reroll before picking,
          // so loop until they pick or skip. Cap iterations so a broken policy
          // that keeps requesting rerolls can't burn infinite gold here.
          ui.beginInterwaveDraft(rng, player);
          logger.logShopOpened('interwave', wave);
          const MAX_INTERWAVE_DECISIONS = 8;
          let closed = false;
          for (let step = 0; step < MAX_INTERWAVE_DECISIONS && !closed; step++) {
            const shopOffer = ui.draftOffer.slice();
            const shopView = {
              wave,
              mode: 'interwave',
              offer: shopOffer,
              costs: shopOffer.map(c => ui.getCardCost(c)),
              player: this._playerView(player),
              gold: player.gold,
              rng,
            };
            const decision = this.policy.decideShop(shopView) || { action: 'skip' };

            if (decision.action === 'pick' && typeof decision.index === 'number') {
              const card = ui.draftOffer[decision.index];
              const pickCost = card ? ui.getCardCost(card) : 0;
              if (card && ui.pickCard(decision.index, player)) {
                noteDeckEmptied(ui, wave);
                logger.logCardPicked(card.id, card.rarity, 'interwave', pickCost, wave);
                logger.logGoldSpent(pickCost);
              }
              closed = true;
            } else if (decision.action === 'reroll') {
              const cost = ui.getRerollCost();
              if (ui.tryReroll(player, rng)) {
                logger.logReroll('interwave', cost, wave);
                logger.logGoldSpent(cost);
              } else {
                // Can't afford to reroll — treat as skip so we don't loop.
                closed = true;
              }
            } else {
              // 'skip' or unknown action — done.
              closed = true;
            }
          }
          logger.logShopClosed('interwave', wave);

          // Move on.
          wave++;
          if (wave > this.maxWaves) break;
          ui.onWaveStart();
          player.respawnAt(world.width / 2, world.height / 2);
          if (wave === this.snapshotAtWave) {
            return {
              snapshot: deepClone({
                world, player, shop, ui, logger,
                policy: this.policy,
                wave, score, stepCount,
                mercyHealUsed: _mercyHealUsed,
                rngState: rng ? rng.getState() : null,
              }),
            };
          }
          spawnCurrentWave();
        }
      }

      stepCount++;
      if (this.maxWaveSec && (stepCount - waveStartStep) * dt > this.maxWaveSec + this.maxWaveSecPerGuard * guards.length) {
        waveStalled = true;
        break;
      }
    }

    // --- Session terminator --------------------------------------
    const died = !player.alive;
    // `wave` post-increments on wave completion, so a run that clears
    // maxWaves exits with wave = maxWaves + 1 even though the player never
    // entered that wave. Clamp for the summary so survival-to-cap reports
    // the last wave actually played.
    const reached = died ? wave : Math.min(wave, this.maxWaves);
    // A run that burns through maxSteps without dying didn't really survive
    // — the loop was cut short by the runaway safety ceiling. Flag it so
    // summarizeBatch can split "legit survived" from "stalled out".
    const timedOut = !died && (stepCount >= this.maxSteps || waveStalled);
    if (died) logger.logDeath(reached, score);
    const finished = logger.endSession({
      died,
      timedOut,
      finalScore: score,
      waveReached: reached,
      totalSteps: stepCount,
    });
    finished.summary.deckEmptiedWave = ui.deckEmptiedWave || 0;
    return finished;
  }

  // Build the player snapshot handed to the policy. Kept minimal —
  // policies should not pull references out of entities, just read
  // `pos`/`alive`/stats.
  _playerView(player) {
    return {
      pos: player.pos,
      hp: player.hp,
      maxHp: player.maxHp,
      gold: player.gold,
      damage: player.damage,
      speed: player.speed,
      radius: player.radius,
      attackRange: player.attackRange,
      attackTimer: player.attackTimer,
      dashCooldownTimer: player.dashCooldownTimer,
      dashCooldownMax: player.dashCooldownMax,
      iframeTimer: player.iframeTimer,
      stickyTimer: player.stickyTimer,
      burnTimer: player.burnTimer,
      superCharge: player.superCharge,
      superReady: player.superReady,
      horseHp: player.horse ? player.horse.hp : 0,
      alive: player.alive,
      lifestealPct: player.lifestealPct,
      thornsPct: player.thornsPct,
      magnetRangeMul: player.magnetRangeMul,
    };
  }
}

// Run N sessions back-to-back with the same policy factory.
// Returns an array of finished session objects.
// `options` is passed through to Simulator — supports `maxWaves`, `commit`,
// `startCards`, `maxSteps`, `seed`. When `seed` is set, each run uses
// `seed + runId` so the batch is reproducible but individual runs explore
// different random paths.
export function runBatch(policyFactory, count, options = {}) {
  const results = [];
  const baseSeed = options.seed != null ? options.seed : null;
  for (let i = 0; i < count; i++) {
    const policy = policyFactory();
    const runSeed = baseSeed != null ? baseSeed + i : null;
    const sim = new Simulator(policy, { ...options, runId: i, seed: runSeed });
    const session = sim.run();
    if (session) results.push(session);
  }
  return results;
}

// Compact aggregate stats from a batch. Useful for quick CLI output.
export function summarizeBatch(sessions) {
  if (sessions.length === 0) {
    return { count: 0 };
  }
  const n = sessions.length;
  const waves = sessions.map(s => s.summary.waveReached);
  const scores = sessions.map(s => s.summary.finalScore);
  const deaths = sessions.filter(s => s.summary.died).length;
  const timedOut = sessions.filter(s => s.summary.timedOut).length;
  // Survival rate counts only runs that actually reached the maxWaves cap
  // alive — timeouts (runaway safety-ceiling exits) are a separate bucket
  // so they don't silently inflate the success number.
  const survived = n - deaths - timedOut;
  const avg = arr => arr.reduce((a, b) => a + b, 0) / arr.length;

  // Most-picked card ids across the batch.
  const pickCounts = {};
  for (const s of sessions) {
    for (const p of s.summary.cardsPicked) {
      pickCounts[p.id] = (pickCounts[p.id] || 0) + 1;
    }
  }

  return {
    count: n,
    deaths,
    timedOut,
    survivalRate: survived / n,
    waveReached: {
      min: Math.min(...waves),
      max: Math.max(...waves),
      mean: avg(waves),
      median: median(waves),
    },
    score: {
      min: Math.min(...scores),
      max: Math.max(...scores),
      mean: avg(scores),
      median: median(scores),
    },
    guardsKilled: avg(sessions.map(s => s.summary.guardsKilled)),
    thornsKillsAvg: avg(sessions.map(s => (s.summary.guardsKilledBySource?.thorns) || 0)),
    meleeKillsAvg: avg(sessions.map(s => (s.summary.guardsKilledBySource?.melee) || 0)),
    damageReflectedAvg: avg(sessions.map(s => s.summary.damageReflected || 0)),
    caravansRobbed: avg(sessions.map(s => s.summary.caravansRobbed)),
    damageTaken: avg(sessions.map(s => s.summary.damageTaken)),
    pickCounts,
    cardImpact: cardImpact(sessions),
  };
}

// For every card that appears in the batch, split sessions into "picked at
// least once" vs "never picked" and compute median wave + median score for
// each cohort. Deltas surface OP cards (big positive wave delta) and trap
// cards (negative delta).
//
// Reliability caveat: cohorts below 5 sessions are flagged with `sparse`
// so the caller can filter them out — small splits are mostly noise.
export function cardImpact(sessions) {
  if (sessions.length === 0) return [];

  // Collect every card id seen anywhere in the batch.
  const cardIds = new Set();
  for (const s of sessions) {
    for (const p of s.summary.cardsPicked) cardIds.add(p.id);
  }

  const results = [];
  for (const id of cardIds) {
    const withCard = sessions.filter(s => s.summary.cardsPicked.some(p => p.id === id));
    const withoutCard = sessions.filter(s => !s.summary.cardsPicked.some(p => p.id === id));
    if (withCard.length === 0 || withoutCard.length === 0) continue;

    // Average stack size among runs that took the card at least once.
    const stackSizes = withCard.map(s => s.summary.cardsPicked.filter(p => p.id === id).length);
    const avgStack = stackSizes.reduce((a, b) => a + b, 0) / stackSizes.length;

    const withWave = median(withCard.map(s => s.summary.waveReached));
    const withoutWave = median(withoutCard.map(s => s.summary.waveReached));
    const withScore = median(withCard.map(s => s.summary.finalScore));
    const withoutScore = median(withoutCard.map(s => s.summary.finalScore));

    results.push({
      id,
      withCount: withCard.length,
      withoutCount: withoutCard.length,
      withMedianWave: withWave,
      withoutMedianWave: withoutWave,
      waveDelta: withWave - withoutWave,
      withMedianScore: withScore,
      withoutMedianScore: withoutScore,
      scoreDelta: withScore - withoutScore,
      avgStack,
      sparse: withCard.length < 5 || withoutCard.length < 5,
    });
  }

  // Biggest positive wave delta first — OP candidates rise to the top.
  results.sort((a, b) => b.waveDelta - a.waveDelta);
  return results;
}

// Combo matrix scan — for every unordered pair of cards, pre-bake stacks of
// both and run a batch. Returns pair deltas sorted by impact on median wave
// so OP synergies (thorns+lifesteal, damage+wideArc, etc.) float to the top.
//
// Why it exists: manual hypothesis-testing ("I bet thorns is OP") doesn't
// scale — last time thorns looked strong to the eye but data showed the
// real winner was damage+lifesteal. Automating the pair sweep replaces
// intuition with evidence.
//
// Method:
//   1. Run `count` baseline sessions (no start cards) to anchor the delta.
//   2. For every pair (a, b) from `cards`, run `count` sessions with
//      start cards `[a×stackSize, b×stackSize]`.
//   3. Compute median/mean wave per pair, delta = pairMedian - baseMedian.
//   4. Sort by delta descending.
//
// Reproducibility: when `seed` is set, each pair uses `seed + (pairIdx+1)*10_000`
// so different pairs explore different random sequences but the whole
// scan is still byte-identical across reruns.
//
// Cost guardrail: with 12 cards and count=50, that's 66 pairs × 50 = 3300
// sessions plus 50 baseline. On smart policy ≈ 0.1s/session, total ≈ 6min.
// Drop `count` or trim `cards` to stay within a tight budget.
export function comboScan(policyFactory, options = {}) {
  const {
    count = 50,
    maxWaves = 30,
    commit = 'sim',
    seed = null,
    cards = null, // null → every card in CARDS
    stackSize = 3,
    onProgress = null, // optional (doneCount, totalPairs) hook
  } = options;

  const cardIds = cards || CARDS.map(c => c.id);
  // Enumerate unordered pairs.
  const pairs = [];
  for (let i = 0; i < cardIds.length; i++) {
    for (let j = i + 1; j < cardIds.length; j++) {
      pairs.push([cardIds[i], cardIds[j]]);
    }
  }

  const t0 = Date.now();

  // Baseline: no pre-baked cards. Seeded with the exact `seed` so reruns
  // of the same scan compare against an identical anchor.
  const baselineSessions = runBatch(policyFactory, count, {
    maxWaves, commit, seed, startCards: [],
  });
  const baselineWaves = baselineSessions.map(s => s.summary.waveReached);
  const baselineMedian = median(baselineWaves);
  const baselineMean = baselineWaves.reduce((a, b) => a + b, 0) / Math.max(1, baselineWaves.length);

  const results = [];
  for (let pi = 0; pi < pairs.length; pi++) {
    const [a, b] = pairs[pi];
    const startCards = [];
    for (let k = 0; k < stackSize; k++) startCards.push(a);
    for (let k = 0; k < stackSize; k++) startCards.push(b);
    // Stride the seed so different pairs aren't replaying the same RNG.
    // +1 so pair 0 doesn't collide with the baseline seed.
    const pairSeed = seed != null ? seed + (pi + 1) * 10_000 : null;
    const sessions = runBatch(policyFactory, count, {
      maxWaves, commit, seed: pairSeed, startCards,
    });
    const waves = sessions.map(s => s.summary.waveReached);
    const med = median(waves);
    const mean = waves.reduce((a, b) => a + b, 0) / Math.max(1, waves.length);
    // Fraction of sessions in this pair that survived to maxWaves — extra
    // confidence signal on top of the median, handy when two pairs tie.
    const survival = sessions.filter(s => !s.summary.died && !s.summary.timedOut).length / sessions.length;
    results.push({
      a, b,
      median: med,
      mean,
      survival,
      n: sessions.length,
      delta: med - baselineMedian,
    });
    if (onProgress) onProgress(pi + 1, pairs.length);
  }

  results.sort((x, y) => y.delta - x.delta || y.mean - x.mean);
  return {
    elapsedMs: Date.now() - t0,
    baseline: {
      median: baselineMedian,
      mean: baselineMean,
      n: baselineWaves.length,
    },
    pairs: results,
    stackSize,
    cardIds,
  };
}

// Per-wave breakdown: "where does the difficulty wall sit and how steep is it?"
//
// For every wave the batch touched, count how many sessions reached the
// start of that wave and how many died inside it, then aggregate medians
// for duration / damage taken / kills / flawless count. A single tall
// jump in mortalityPct between wave N and N+1 marks the stall point.
//
// Why walk events instead of trusting summary.waveReached? The simulator
// post-increments `wave` on wave completion, so a run that clears the
// maxWaves cap ends with waveReached = maxWaves + 1. Walking events gives
// the actual last wave the player entered.
export function perWaveStats(sessions) {
  const waves = new Map();

  const getBucket = (w) => {
    let acc = waves.get(w);
    if (!acc) {
      acc = {
        reachedCount: 0,
        diedHere: 0,
        durations: [],
        damages: [],
        kills: [],
        flawlessCount: 0,
      };
      waves.set(w, acc);
    }
    return acc;
  };

  for (const s of sessions) {
    const killsByWave = {};
    const endByWave = {};
    const flawlessWaves = new Set();
    let lastStarted = 0;

    for (const ev of s.events) {
      if (ev.type === 'wave_start') {
        if (ev.wave > lastStarted) lastStarted = ev.wave;
      } else if (ev.type === 'guard_killed') {
        const w = ev.wave;
        if (w) killsByWave[w] = (killsByWave[w] || 0) + 1;
      } else if (ev.type === 'wave_end') {
        endByWave[ev.wave] = {
          duration: ev.durationMs || 0,
          damage: ev.damageTaken || 0,
        };
      } else if (ev.type === 'flawless') {
        flawlessWaves.add(ev.wave);
      }
    }

    if (lastStarted === 0) continue;
    const died = !!s.summary.died;

    for (let w = 1; w <= lastStarted; w++) {
      const acc = getBucket(w);
      acc.reachedCount++;
      if (died && w === lastStarted) acc.diedHere++;
      const end = endByWave[w];
      if (end) {
        acc.durations.push(end.duration);
        acc.damages.push(end.damage);
      }
      acc.kills.push(killsByWave[w] || 0);
      if (flawlessWaves.has(w)) acc.flawlessCount++;
    }
  }

  const result = [];
  const sortedWaves = [...waves.keys()].sort((a, b) => a - b);
  for (const w of sortedWaves) {
    const acc = waves.get(w);
    result.push({
      wave: w,
      reachedCount: acc.reachedCount,
      diedHere: acc.diedHere,
      mortalityPct: acc.reachedCount > 0 ? (acc.diedHere / acc.reachedCount) * 100 : 0,
      medianDuration: median(acc.durations),
      medianDamageTaken: median(acc.damages),
      medianKills: median(acc.kills),
      flawlessCount: acc.flawlessCount,
    });
  }
  return result;
}
